import { UpdateArticleFolderName } from '../../articleFolder/editArticleFolder/updateArticleFolderName';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';
import { DeleteRecord } from '../../deleteRecord';
import { useTranslation } from 'react-i18next';
import { useListBackNavigation } from '../../../hooks/listNavigation';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { usePdfPreview } from '../../../hooks/usePdfPreview';
import OpenPDFDialog from '../../openPDFDialog';
import Iconify from '../../Iconify';
import OpenPublicPageAction from '../../publicPage/OpenPublicPageAction';
import axios from 'axios';
import '../../../css/roles/roleDetailsHeader.css';

const ArticleFolderDetailsHeader = () => {

    const { t } = useTranslation();
    const { goBack, goToList } = useListBackNavigation('/dashboard/dossiers_articles');
    const isDesktop = useResponsive('up', 'lg');

    const articleFolderDetailsData = useSelector((state) => state.ArticleFolderDetailsData);

    const hasArticleFolderUpdatePermission = useHasPermission('dossier_update');
    const hasArticleFolderDeletePermission = useHasPermission('dossier_delete');

    const [updateArticleFolderOpen, setUpdateArticleFolderOpen] = useState(false);
    const [deleteArticleFolderOpen, setDeleteArticleFolderOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [errMsg, setErrMsg] = useState(null);

    const openActionMenu = Boolean(anchorEl);

    const {
        dialogProps,
        close: closePdfPreview,
        openLoadingPreview,
        openFromAxiosBlobResponse,
    } = usePdfPreview();

    const handleDeleteArticleFolder = async () => {
        try {
            const response = await axios.delete(`article-folder/${articleFolderDetailsData.id}`);
            if (response.status === 200 || response.status === 201) {
                setDeleteArticleFolderOpen(false);
                goToList();
                setErrMsg('');
            }
        } catch (error) {
            setErrMsg(error.response?.data?.message || error.message || t('Une erreur est survenue lors de la suppression de dossier.'));
            console.error('Error deleting articleFolder:', error);
            CustomErrorLogger(error, "Delete articleFolder in ArticleFolderDetailsHeader");
        }
    }

    const handleGetArticleQrCode = async () => {
        const fallbackFileName = `${(articleFolderDetailsData?.name || 'dossier-article').replace(/[^a-zA-Z0-9-_]/g, '_')}-qr-code.pdf`;
        try {
            openLoadingPreview({
                title: 'QR code dossier',
                fileName: fallbackFileName,
            });
            const response = await axios.get(`article-folder-qr-code/${articleFolderDetailsData?.id}`, { responseType: 'blob' });
            if (response.status === 200 || response.status === 201) {
                openFromAxiosBlobResponse(response, {
                    fallbackFileName,
                    title: 'QR code dossier',
                });
            }
        } catch (error) {
            closePdfPreview();
            console.error(error, 'Error getting article folder QR code');
            CustomErrorLogger(error, 'Error getting article folder QR code');
        }
    };

    const handleOpenActionMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseActionMenu = () => {
        setAnchorEl(null);
    };

    const handleGoBack = goBack;

    return (
        <Box className="roleDetailsHeader_mainBox">
            <Box className="roleDetailsHeader_firstPart">
                <Iconify className='roleDetailsHeader_back_icon' icon={"material-symbols:arrow-back"} onClick={handleGoBack} />
                <Typography className="roleDetailsHeader_title">{articleFolderDetailsData?.title}</Typography>
                {hasArticleFolderUpdatePermission && <Iconify className='roleDetailsHeader_edit_icon' icon={"ic:outline-edit"} onClick={() => setUpdateArticleFolderOpen(true)} />}
            </Box>
            <Box className="roleDetailsHeader_secondPart">
                {isDesktop && hasArticleFolderUpdatePermission &&
                    <Box className="MachineDetailsHeader_action_box" onClick={handleGetArticleQrCode}>
                        <Iconify className='MachineDetailsHeader_copy_icon' icon="ic:baseline-qrcode" />
                        <Typography className="MachineDetailsHeader_copy_title">{t('QRcode')}</Typography>
                    </Box>
                }
                {isDesktop && hasArticleFolderUpdatePermission &&
                    <OpenPublicPageAction endpoint="article-folder-qr-code" entityId={articleFolderDetailsData?.id} />
                }
                {isDesktop && hasArticleFolderDeletePermission &&
                    <Box className="roleDetailsHeader_action_box" onClick={() => setDeleteArticleFolderOpen(true)}>
                        <Iconify className='roleDetailsHeader_delete_icon' icon={"ic:outline-delete"} />
                        <Typography className="roleDetailsHeader_delete_title">{t('Supprimer')}</Typography>
                    </Box>
                }

                {!isDesktop && <Iconify
                    className='roleDetailsHeader_dots_icon'
                    icon={"vaadin:ellipsis-dots-v"}
                    id="userPageActionMenuIcom"
                    aria-controls={openActionMenu ? 'long-menu' : undefined}
                    aria-expanded={openActionMenu ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleOpenActionMenu}
                />
                }
                <Menu
                    id="usersePageActionMenu"
                    MenuListProps="usersPageActionMenuIcom"
                    open={openActionMenu}
                    anchorEl={anchorEl}
                    onClose={handleCloseActionMenu}
                    onClick={handleCloseActionMenu}
                    slotProps={{ paper: { style: { width: '30ch', backgroundColor: '#fffff' } } }}
                >
                    {!isDesktop && hasArticleFolderDeletePermission &&
                        <MenuItem className='roleDetailsHeader_menuItem' onClick={() => setDeleteArticleFolderOpen(true)}>
                            <Iconify className='roleDetailsHeader_delete_icon' icon={"ic:outline-delete"} />
                            <Typography className="roleDetailsHeader_delete_title">{t('Supprimer')}</Typography>
                        </MenuItem>
                    }

                    {!isDesktop && hasArticleFolderUpdatePermission &&
                        <MenuItem className='MachineDetailsHeader_menuItem' onClick={handleGetArticleQrCode}>
                            <Iconify className='MachineDetailsHeader_copy_icon' icon="ic:baseline-qrcode" />
                            <Typography className="MachineDetailsHeader_copy_title">{t('QRcode')}</Typography>
                        </MenuItem>
                    }
                    {!isDesktop && hasArticleFolderUpdatePermission &&
                        <OpenPublicPageAction
                            asMenuItem
                            endpoint="article-folder-qr-code"
                            entityId={articleFolderDetailsData?.id}
                        />
                    }
                </Menu>
            </Box>

            {updateArticleFolderOpen &&
                <UpdateArticleFolderName
                    updateArticleFolderOpen={updateArticleFolderOpen}
                    setUpdateArticleFolderOpen={setUpdateArticleFolderOpen}
                />
            }

            {deleteArticleFolderOpen &&
                <DeleteRecord
                    errMsg={errMsg}
                    setErrMsg={setErrMsg}
                    openDelete={deleteArticleFolderOpen}
                    setOpenDelete={setDeleteArticleFolderOpen}
                    handleDelete={handleDeleteArticleFolder}
                    title="Êtes-vous sûr de vouloir supprimer ce dossier ?"
                />
            }

            <OpenPDFDialog {...dialogProps} />
        </Box>
    );
}

export default ArticleFolderDetailsHeader;